import { CloseOutlined } from "@ant-design/icons";
import { Dialog, DialogContent } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import React, { useContext, useEffect, useRef, useState } from "react";
import useFBOApi from "../../services/api/useFBOApi";
import { StorageContext } from "../../services/StorageProvider";
import "../css/SearchAirlineDialog.css";
import Loader from "../element/Loader";

const SearchAirlineDialog = ({ open, onClose, onSelectAirline }) => {
  const { airlineListData } = useContext(StorageContext);
  const [searchText, setSearchText] = useState("");
  const [searchedAirline, setSearchedAirline] = useState([]);
  const currentSearchRef = useRef("");
  const { searchAirline } = useFBOApi();

  const searchAirlineMutation = useMutation({
    mutationFn: async ({ data, searchTerm }) => {
      const response = await searchAirline({
        searchTerm: data,
        limit: 100,
      });
      return { response, searchTerm };
    },
    onSuccess: ({ response, searchTerm }) => {
      // Only update if this response is for the current search term
      if (searchTerm === currentSearchRef.current) {
        setSearchedAirline(response?.data);
      }
    },
  });

  useEffect(() => {
    setSearchedAirline(sortedAirlineList(airlineListData?.data));
  }, [airlineListData]);

  const sortedAirlineList = (airlineList) => {
    const sortedAirline = airlineList?.sort((a, b) => {
      const nameA = a?.airlineName.toLowerCase();
      const nameB = b?.airlineName.toLowerCase();
      return nameA.localeCompare(nameB);
    });
    return sortedAirline;
  };

  const handleAirlineSearchChange = (event) => {
    const text = event.target.value;
    setSearchText(text);
    currentSearchRef.current = text; // Always update the ref with current text

    // If text is empty or too short, show original data immediately
    if (text.length === 0 || text.length <= 2) {
      // Cancel any pending mutations and reset state
      searchAirlineMutation.reset();
      setSearchedAirline(sortedAirlineList(airlineListData?.data));
    } else {
      // Only search when text is longer than 2 characters
      searchAirlineMutation.mutate({
        data: text,
        searchTerm: text, // Pass searchTerm to mutation
      });
    }
  };

  const handleSelectAirline = (airline) => {
    onSelectAirline(airline);
    onClose();
  };

  return (
    <Dialog
      open={open ?? false}
      onClose={onClose}
      PaperProps={{
        className: "search-airline-dialog-container",
        sx: { borderRadius: "20px" },
      }}
    >
      <DialogContent>
        <CloseOutlined
          className="search-airline-dialog-close"
          onClick={onClose}
        />
        <div className="search-airline-dialog-content">
          <div className="search-airline-dialog-title">Search Airline</div>
          <input
            placeholder="Search Airline"
            value={searchText}
            onChange={handleAirlineSearchChange}
            maxLength={30}
          ></input>
          <div className="search-airline-dialog-list">
            {searchAirlineMutation?.isPending && searchText.length > 2 ? (
              <Loader />
            ) : searchedAirline?.length > 0 ? (
              searchedAirline?.map((airline) => (
                <div
                  key={airline?.id}
                  className="search-airline-dialog-list-item"
                  onClick={() => handleSelectAirline(airline)}
                >
                  {airline?.airlineName} ({airline?.iataCode ?? airline?.icaoCode ?? "NA"})
                </div>
              ))
            ) : (
              <div className="search-airline-dialog-no-results">
                No results found
              </div>
            )}
            {}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchAirlineDialog;
